import type {
  ShallowTaskWorkflow,
  ShallowTaskWorkflowGroup,
} from '@adc/sdk/types'

/**
 * Priority order for workflow types — higher index = farther down the funnel = more pertinent.
 */
const WORKFLOW_TYPE_PRIORITY: string[] = [
  'INTAKE',
  'MARKETPLACE_ACTIVATION',
  'SELLING',
  'CLOSING',
]

/** Icons per workflow domain */
export const WORKFLOW_TYPE_ICONS: Record<string, string> = {
  INTAKE: 'arrow-right-to-bracket',
  MARKETPLACE_ACTIVATION: 'store',
  SELLING: 'gavel',
  CLOSING: 'handshake',
}

/** Labels per workflow domain */
export const WORKFLOW_TYPE_LABELS: Record<string, string> = {
  INTAKE: 'Intake',
  MARKETPLACE_ACTIVATION: 'Marketplace Activation',
  SELLING: 'Selling',
  CLOSING: 'Closing',
}

export interface GroupedWorkflow {
  workflowType: string
  taskGroups: ShallowTaskWorkflowGroup[]
}

export type WorkflowDomainStatus = 'ongoing' | 'complete' | 'upcoming'

/**
 * Groups task_groups by their workflow_type field.
 * Returns array in funnel order (INTAKE first, CLOSING last).
 */
export function groupTaskGroupsByWorkflowType(
  workflow: ShallowTaskWorkflow | null
): GroupedWorkflow[] {
  if (!workflow?.task_groups) return []

  const grouped = new Map<string, ShallowTaskWorkflowGroup[]>()
  for (const group of workflow.task_groups) {
    const wfType = group.workflow_type ?? 'UNKNOWN'
    if (!grouped.has(wfType)) grouped.set(wfType, [])
    grouped.get(wfType)!.push(group)
  }

  // Sort by funnel priority
  return Array.from(grouped.entries())
    .sort(([a], [b]) => {
      const aIdx = WORKFLOW_TYPE_PRIORITY.indexOf(a)
      const bIdx = WORKFLOW_TYPE_PRIORITY.indexOf(b)
      return (aIdx === -1 ? 999 : aIdx) - (bIdx === -1 ? 999 : bIdx)
    })
    .map(([workflowType, taskGroups]) => ({ workflowType, taskGroups }))
}

/**
 * Returns the domain status derived from task_group statuses.
 * - 'ongoing': at least one task_group is ONGOING
 * - 'complete': all task_groups are COMPLETE
 * - 'upcoming': no tasks started yet
 */
export function getDomainStatus(
  taskGroups: ShallowTaskWorkflowGroup[]
): WorkflowDomainStatus {
  const allComplete = taskGroups.every(g => g.status === 'COMPLETE')
  if (allComplete) return 'complete'

  const anyOngoing = taskGroups.some(g => g.status === 'ONGOING')
  if (anyOngoing) return 'ongoing'

  return 'upcoming'
}
